/**
 * Race simulation.
 *
 * A single deterministic `RaceSim` drives both single-player (in the browser)
 * and online races (on the authoritative server). It owns vehicle stepping, AI,
 * car-to-car collisions, checkpoint ordering, lap validation, live positions,
 * drift scoring, elimination and the reward calculation.
 */

import { clamp, formatTime, makeRng } from './math.js';
import { CAR_BY_ID, getCar, type CarHandling } from './cars.js';
import { computeHandling, type UpgradeLevels } from './upgrades.js';
import { buildTrack, nearestPathIndexNear, type BuiltTrack } from './track.js';
import {
  createVehicleState, makeInput, resolveCarCollision, respawnOnPath, stepVehicle,
  type VehicleInput, type VehicleState,
} from './vehicle.js';
import { createAIState, updateAI, type AIDifficulty, type AIState, type AIWorld } from './ai.js';
import type { RaceModeId } from './net.js';

export interface RaceConfig {
  mode: RaceModeId;
  trackId: string;
  laps: number;
  aiCount: number;
  aiDifficulty: AIDifficulty;
  seed: number;
  /** Seconds of countdown before the lights go out. */
  countdown: number;
  /** Eliminations happen on this interval (seconds) in elimination mode. */
  eliminationInterval: number;
  /** Total checkpoints to pass in checkpoint-sprint mode. */
  checkpointTarget: number;
}

export const DEFAULT_RACE_CONFIG: RaceConfig = {
  mode: 'quick-race',
  trackId: 'sunny-acres',
  laps: 3,
  aiCount: 5,
  aiDifficulty: 'normal',
  seed: 1337,
  countdown: 3.2,
  eliminationInterval: 30,
  checkpointTarget: 6,
};

export interface RacerInit {
  id: string;
  name: string;
  carId: string;
  color: string;
  ai: boolean;
  difficulty?: AIDifficulty;
  upgrades?: UpgradeLevels;
  isPlayer?: boolean;
}

export interface Racer {
  id: string;
  name: string;
  carId: string;
  color: string;
  ai: boolean;
  difficulty: AIDifficulty;
  isPlayer: boolean;
  handling: CarHandling;
  upgrades: UpgradeLevels;
  vehicle: VehicleState;
  input: VehicleInput;
  aiState: AIState | null;

  /** Lap currently being driven (1-based); 0 until the line is first crossed. */
  lap: number;
  lapsCompleted: number;
  /** Next gate the racer must pass (index into track.checkpoints). */
  checkpoint: number;
  /** Non-start gates passed, which is what checkpoint-sprint counts. */
  checkpointsPassed: number;
  /** Whether the start line has been crossed at least once. */
  armed: boolean;
  /** Signed distance to the expected gate on the previous step. */
  gatePrevSide: number;

  position: number;
  finished: boolean;
  finishTime: number;
  eliminated: boolean;

  lapTimes: number[];
  bestLap: number;
  lastLapTime: number;
  lapStartTime: number;

  progress: number;
  maxProgress: number;
  /**
   * Last centreline sample this racer was near. Used as the search hint so a
   * car alongside a hairpin is never mistaken for one on the other leg.
   */
  pathIndex: number;

  driftScore: number;
  pendingDrift: number;
  driftCombo: number;
  driftIdle: number;

  offTrackTime: number;
  cleanLap: boolean;
  respawns: number;
  crashes: number;
  wrongWay: boolean;
  wrongWayTime: number;
  /** Laps that were invalidated by cutting the course. */
  invalidLaps: number;
}

export type RaceEvent =
  | { type: 'lap'; racerId: string; lap: number; lapTime: number; best: boolean; valid: boolean }
  | { type: 'finish'; racerId: string; position: number; time: number }
  | { type: 'crash'; racerId: string; impact: number; x: number; y: number }
  | { type: 'eliminated'; racerId: string }
  | { type: 'checkpoint'; racerId: string; index: number }
  | { type: 'countdown'; value: number }
  | { type: 'start' }
  | { type: 'achievement'; id: string };

export interface RaceResult {
  id: string;
  name: string;
  carId: string;
  color: string;
  ai: boolean;
  position: number;
  laps: number;
  time: number;
  bestLap: number;
  lapTimes: number[];
  finished: boolean;
  eliminated: boolean;
  driftScore: number;
  respawns: number;
  topSpeed: number;
  offTrackTime: number;
}

export interface RewardBreakdown {
  money: number;
  xp: number;
  base: number;
  positionBonus: number;
  lapBonus: number;
  driftBonus: number;
  difficultyBonus: number;
  bestLapBonus: number;
  newBestLap: boolean;
}

export class RaceSim {
  readonly config: RaceConfig;
  readonly track: BuiltTrack;
  readonly racers: Racer[] = [];
  readonly events: RaceEvent[] = [];
  /** Simulation time since the lights went out. */
  time = 0;
  countdown: number;
  phase: 'countdown' | 'racing' | 'finished' = 'countdown';
  private rng: () => number;
  private othersPool: Array<{ x: number; y: number; vx: number; vy: number; speed: number }> = [];
  private aiWorld: AIWorld;
  private frozenInput: VehicleInput = makeInput();
  /** Used while the lights are still on: cars hold still on the grid. */
  private gridHold: VehicleInput = makeInput();
  private eliminationTimer = 0;
  private eliminationOrder: string[] = [];
  private topSpeeds = new Map<string, number>();

  constructor(config: Partial<RaceConfig>, inits: RacerInit[]) {
    this.config = { ...DEFAULT_RACE_CONFIG, ...config };
    this.track = buildTrack(this.config.trackId);
    this.rng = makeRng(this.config.seed >>> 0);
    this.countdown = this.config.countdown;
    for (const init of inits) this.addRacer(init);
    this.othersPool = inits.map(() => ({ x: 0, y: 0, vx: 0, vy: 0, speed: 0 }));
    this.aiWorld = { track: this.track, others: this.othersPool, otherCount: 0 };
    this.resetGrid();
    this.updatePositions();
  }

  addRacer(init: RacerInit): Racer {
    const car = CAR_BY_ID[init.carId] ?? getCar(init.carId);
    const upgrades = init.upgrades ?? {};
    const racer: Racer = {
      id: init.id,
      name: init.name,
      carId: car.id,
      color: init.color,
      ai: init.ai,
      difficulty: init.difficulty ?? this.config.aiDifficulty,
      isPlayer: init.isPlayer ?? false,
      handling: computeHandling(car, upgrades),
      upgrades,
      vehicle: createVehicleState(0, 0, 0),
      input: makeInput(),
      aiState: init.ai ? createAIState(init.difficulty ?? this.config.aiDifficulty, (this.config.seed + this.racers.length * 7919) >>> 0) : null,
      lap: 0,
      lapsCompleted: 0,
      checkpoint: 0,
      checkpointsPassed: 0,
      armed: false,
      gatePrevSide: -1,
      position: this.racers.length + 1,
      finished: false,
      finishTime: 0,
      eliminated: false,
      lapTimes: [],
      bestLap: Infinity,
      lastLapTime: 0,
      lapStartTime: 0,
      progress: -this.track.totalLength,
      maxProgress: -this.track.totalLength,
      pathIndex: 0,
      driftScore: 0,
      pendingDrift: 0,
      driftCombo: 1,
      driftIdle: 0,
      offTrackTime: 0,
      cleanLap: true,
      respawns: 0,
      crashes: 0,
      wrongWay: false,
      wrongWayTime: 0,
      invalidLaps: 0,
    };
    this.racers.push(racer);
    return racer;
  }

  /** Places every racer on the starting grid in a staggered formation. */
  resetGrid(): void {
    const spawns = this.track.spawns;
    this.racers.forEach((racer, i) => {
      const slot = spawns[i % spawns.length]!;
      const v = racer.vehicle;
      v.x = slot.x;
      v.y = slot.y;
      v.angle = slot.angle;
      v.vx = 0;
      v.vy = 0;
      v.vLong = 0;
      v.vLat = 0;
      v.yawRate = 0;
      v.steerAngle = 0;
      v.turboCharge = 1;
      racer.lap = 0;
      racer.lapsCompleted = 0;
      racer.checkpoint = 0;
      racer.checkpointsPassed = 0;
      racer.armed = false;
      racer.gatePrevSide = -1;
      racer.progress = -this.track.totalLength;
      racer.maxProgress = -this.track.totalLength;
      racer.pathIndex = slot.index;
      racer.finished = false;
      racer.eliminated = false;
      racer.finishTime = 0;
      racer.lapTimes = [];
      racer.bestLap = Infinity;
      racer.lapStartTime = 0;
      racer.driftScore = 0;
      racer.pendingDrift = 0;
      racer.driftCombo = 1;
      racer.driftIdle = 0;
      racer.offTrackTime = 0;
      racer.cleanLap = true;
      racer.wrongWayTime = 0;
      racer.wrongWay = false;
    });
    this.time = 0;
    this.phase = 'countdown';
    this.countdown = this.config.countdown;
    this.eliminationTimer = 0;
    this.eliminationOrder = [];
    this.topSpeeds.clear();
  }

  getRacer(id: string): Racer | undefined {
    return this.racers.find((r) => r.id === id);
  }

  setInput(id: string, input: VehicleInput): void {
    const racer = this.getRacer(id);
    if (!racer) return;
    racer.input.throttle = clamp(input.throttle, 0, 1);
    racer.input.brake = clamp(input.brake, 0, 1);
    racer.input.steer = clamp(input.steer, -1, 1);
    racer.input.handbrake = !!input.handbrake;
    racer.input.boost = !!input.boost;
  }

  /** Advances the whole race by one fixed timestep. */
  step(dt: number): void {
    this.events.length = 0;
    if (this.phase === 'countdown') {
      const before = Math.ceil(this.countdown);
      this.countdown -= dt;
      const after = Math.ceil(this.countdown);
      if (after !== before && after >= 0) this.events.push({ type: 'countdown', value: after });
      if (this.countdown <= 0) {
        this.countdown = 0;
        this.phase = 'racing';
        this.time = 0;
        for (const r of this.racers) r.lapStartTime = 0;
        this.events.push({ type: 'start' });
      }
      // Cars sit on the grid while the lights are on. The hold input must be
      // genuinely neutral (not "brake"), because brake at standstill is the
      // reverse gear — the whole field used to roll backwards off the line.
      const hold = this.gridHold;
      hold.throttle = 0;
      hold.brake = 0;
      hold.steer = 0;
      hold.handbrake = false;
      hold.boost = false;
      for (const racer of this.racers) {
        stepVehicle(racer.vehicle, racer.handling, hold, this.track, dt);
        // Settling is fine; creeping is not. Pin the car to its grid slot.
        const v = racer.vehicle;
        v.vx = 0;
        v.vy = 0;
        v.vLong = 0;
        v.vLat = 0;
        v.yawRate = 0;
      }
      return;
    }

    this.time += dt;
    if (this.phase === 'racing') this.updateAIInputs(dt);
    this.stepPhysics(dt);
    if (this.phase === 'racing') {
      this.updateProgress(dt);
      this.updatePositions();
      this.updateElimination(dt);
      if (this.racers.every((r) => r.finished || r.eliminated)) this.phase = 'finished';
    }
  }

  private updateAIInputs(dt: number): void {
    for (let i = 0; i < this.racers.length; i++) {
      const racer = this.racers[i]!;
      if (!racer.aiState) continue;
      let n = 0;
      for (let j = 0; j < this.racers.length; j++) {
        if (i === j) continue;
        const other = this.racers[j]!;
        const slot = this.othersPool[n++]!;
        slot.x = other.vehicle.x;
        slot.y = other.vehicle.y;
        slot.vx = other.vehicle.vx;
        slot.vy = other.vehicle.vy;
        slot.speed = other.vehicle.speed;
      }
      this.aiWorld.others = this.othersPool;
      this.aiWorld.otherCount = n;
      updateAI(racer.aiState, racer.vehicle, racer.handling, this.aiWorld, dt, racer.input);
    }
  }

  private stepPhysics(dt: number): void {
    for (const racer of this.racers) {
      if (racer.eliminated) continue;
      const frozen = racer.finished || this.phase === 'countdown';
      if (frozen) {
        this.frozenInput.throttle = 0;
        this.frozenInput.brake = racer.finished ? 0.35 : 0;
        this.frozenInput.steer = 0;
        this.frozenInput.handbrake = false;
        this.frozenInput.boost = false;
      }
      const input = frozen ? this.frozenInput : racer.input;
      const res = stepVehicle(racer.vehicle, racer.handling, input, this.track, dt);
      if (res.wallHit && res.impact > 45) {
        racer.crashes++;
        racer.pendingDrift = 0;
        racer.driftCombo = 1;
        this.events.push({ type: 'crash', racerId: racer.id, impact: res.impact, x: racer.vehicle.x, y: racer.vehicle.y });
      }
      const top = this.topSpeeds.get(racer.id) ?? 0;
      if (racer.vehicle.speed > top) this.topSpeeds.set(racer.id, racer.vehicle.speed);
      // Drift scoring.
      if (this.phase === 'racing' && !racer.finished) this.updateDrift(racer, dt);
    }

    // Car-to-car collisions (equal and opposite impulses, mass weighted).
    for (let i = 0; i < this.racers.length; i++) {
      const a = this.racers[i]!;
      if (a.eliminated) continue;
      for (let j = i + 1; j < this.racers.length; j++) {
        const b = this.racers[j]!;
        if (b.eliminated) continue;
        const impact = resolveCarCollision(
          a.vehicle, a.handling.mass, a.handling.width * 0.55,
          b.vehicle, b.handling.mass, b.handling.width * 0.55,
        );
        if (impact > 40) {
          for (const r of [a, b]) {
            r.pendingDrift = 0;
            r.driftCombo = 1;
          }
        }
      }
    }
  }

  private updateDrift(racer: Racer, dt: number): void {
    const v = racer.vehicle;
    const drifting = v.slip > 0.32 && v.speed > 70;
    if (drifting) {
      racer.pendingDrift += v.slip * v.speed * dt * 0.06 * racer.driftCombo;
      racer.driftIdle = 0;
      racer.driftCombo = Math.min(5, racer.driftCombo + dt * 0.45);
    } else {
      racer.driftIdle += dt;
      if (racer.driftIdle > 0.8 && racer.pendingDrift > 0) {
        racer.driftScore += Math.round(racer.pendingDrift);
        racer.pendingDrift = 0;
        racer.driftCombo = 1;
      }
    }
  }

  private updateProgress(dt: number): void {
    const gates = this.track.checkpoints;
    if (gates.length === 0) return;
    for (const racer of this.racers) {
      if (racer.eliminated) continue;
      const v = racer.vehicle;
      const dist = nearestPathIndexNear(this.track, v.x, v.y, racer.pathIndex);
      racer.pathIndex = dist;
      const pathPoint = this.track.path[dist]!;
      // Cars still sitting on the grid are *behind* the start line, so their
      // distance along the lap is negative. Without this a car that has not
      // moved would out-rank one that is already halfway round the lap.
      const candidate = racer.armed
        ? racer.lapsCompleted * this.track.totalLength + pathPoint.dist
        : pathPoint.dist - this.track.totalLength;
      if (candidate > racer.maxProgress) racer.maxProgress = candidate;
      racer.progress = racer.maxProgress;

      // Off-track bookkeeping (used for clean-lap bonuses and warnings).
      if (!v.onRoad && !racer.finished) racer.offTrackTime += dt;

      // Wrong-way detection relative to the path tangent.
      const tangent = pathPoint.angle;
      const forward = Math.cos(v.angle - tangent);
      if (forward < -0.35) tracer_wrongWay(racer, dt);
      else racer.wrongWayTime = Math.max(0, racer.wrongWayTime - dt * 2);

      if (racer.finished || this.phase !== 'racing') continue;

      const gate = gates[racer.checkpoint % gates.length]!;
      const tx = Math.cos(gate.angle);
      const ty = Math.sin(gate.angle);
      const side = (v.x - gate.x) * tx + (v.y - gate.y) * ty;
      const lateral = Math.abs(-(v.x - gate.x) * ty + (v.y - gate.y) * tx);
      if (racer.gatePrevSide < 0 && side >= 0 && lateral < gate.halfWidth * 1.6) {
        this.onGateCrossed(racer, gate.index, lateral);
      }
      racer.gatePrevSide = side;
    }
  }

  private onGateCrossed(racer: Racer, gateIndex: number, lateral: number): void {
    const gates = this.track.checkpoints;
    const isStartLine = gateIndex === gates[0]!.index;
    const crossingStart = racer.checkpoint === 0;
    if (isStartLine && crossingStart) {
      if (!racer.armed) {
        racer.armed = true;
        racer.lap = 1;
        racer.lapStartTime = this.time;
        racer.cleanLap = true;
        racer.offTrackTime = 0;
        racer.checkpoint = 1 % gates.length;
        return;
      }
      const lapTime = this.time - racer.lapStartTime;
      racer.lapsCompleted++;
      racer.lastLapTime = lapTime;
      racer.lapTimes.push(lapTime);
      const valid = racer.offTrackTime < 2.5;
      if (!valid) racer.invalidLaps++;
      let best = false;
      if (valid && lapTime < racer.bestLap) {
        racer.bestLap = lapTime;
        best = true;
      }
      this.events.push({ type: 'lap', racerId: racer.id, lap: racer.lapsCompleted, lapTime, best, valid });
      racer.offTrackTime = 0;
      racer.cleanLap = true;
      if (racer.lapsCompleted >= this.config.laps) {
        racer.finished = true;
        racer.finishTime = this.time;
        racer.lap = this.config.laps;
        this.events.push({ type: 'finish', racerId: racer.id, position: 0, time: racer.finishTime });
      } else {
        racer.lap = racer.lapsCompleted + 1;
        racer.lapStartTime = this.time;
        racer.checkpoint = 1 % gates.length;
      }
      return;
    }

    if (racer.checkpoint === 0) return;
    const expected = gates[racer.checkpoint]!;
    if (gateIndex !== expected.index) return;
    this.events.push({ type: 'checkpoint', racerId: racer.id, index: racer.checkpoint });
    racer.checkpoint = (racer.checkpoint + 1) % gates.length;

    // Checkpoint Sprint is decided on gates rather than laps.
    if (this.config.mode === 'checkpoint') {
      racer.checkpointsPassed++;
      if (racer.checkpointsPassed >= this.config.checkpointTarget) {
        racer.finished = true;
        racer.finishTime = this.time;
        racer.lap = Math.max(1, racer.lap);
        this.events.push({ type: 'finish', racerId: racer.id, position: racer.position, time: racer.finishTime });
      }
    }
    void lateral;
  }

  private updatePositions(): void {
    const sorted = [...this.racers].sort((a, b) => {
      if (a.eliminated !== b.eliminated) return a.eliminated ? 1 : -1;
      if (a.finished && b.finished) return a.finishTime - b.finishTime;
      if (a.finished !== b.finished) return a.finished ? -1 : 1;
      return b.progress - a.progress;
    });
    sorted.forEach((r, i) => {
      r.position = i + 1;
    });
  }

  private updateElimination(dt: number): void {
    if (this.config.mode !== 'elimination') return;
    const active = this.racers.filter((r) => !r.eliminated && !r.finished);
    if (active.length <= 1) return;
    this.eliminationTimer += dt;
    if (this.eliminationTimer < this.config.eliminationInterval) return;
    this.eliminationTimer = 0;
    const sorted = active.sort((a, b) => a.progress - b.progress);
    const victim = sorted[0]!;
    victim.eliminated = true;
    victim.finishTime = this.time;
    this.eliminationOrder.push(victim.id);
    this.events.push({ type: 'eliminated', racerId: victim.id });
  }

  /** Puts a racer back on the racing line facing the right way. */
  respawn(racerId: string): void {
    const racer = this.getRacer(racerId);
    if (!racer) return;
    const idx = nearestPathIndexNear(this.track, racer.vehicle.x, racer.vehicle.y, racer.pathIndex);
    respawnOnPath(racer.vehicle, this.track, idx);
    racer.pathIndex = idx;
    racer.respawns++;
    racer.pendingDrift = 0;
    racer.driftCombo = 1;
  }

  results(): RaceResult[] {
    this.updatePositions();
    return [...this.racers]
      .sort((a, b) => a.position - b.position)
      .map((r) => ({
        id: r.id,
        name: r.name,
        carId: r.carId,
        color: r.color,
        ai: r.ai,
        position: r.position,
        laps: r.lapsCompleted,
        time: r.finished ? r.finishTime : this.time,
        bestLap: Number.isFinite(r.bestLap) ? r.bestLap : 0,
        lapTimes: [...r.lapTimes],
        finished: r.finished,
        eliminated: r.eliminated,
        driftScore: Math.round(r.driftScore),
        respawns: r.respawns,
        topSpeed: this.topSpeeds.get(r.id) ?? 0,
        offTrackTime: r.offTrackTime,
      }));
  }

  isComplete(): boolean {
    return this.phase === 'finished';
  }

  /** Leader's race time, used by the HUD for interval display. */
  leaderTime(): number {
    let t = 0;
    for (const r of this.racers) if (r.finished) t = Math.max(t, r.finishTime);
    return t;
  }
}

function tracer_wrongWay(racer: Racer, dt: number): void {
  racer.wrongWayTime += dt;
  racer.wrongWay = racer.wrongWayTime > 1.1;
}

/**
 * Reward calculation. Uses the finishing position, lap count, drift score and
 * opponent strength so that beating stronger fields pays more.
 */
export function raceRewards(
  result: RaceResult,
  config: RaceConfig,
  opponentCount: number,
  difficulty: AIDifficulty,
  previousBestLap: number,
): RewardBreakdown {
  const base = 420;
  const positionBonus = Math.max(0, opponentCount + 1 - result.position) * 170;
  const lapBonus = result.laps * 65;
  const driftBonus = Math.floor(result.driftScore / 12);
  const difficultyScale: Record<AIDifficulty, number> = {
    beginner: 0.5, easy: 0.72, normal: 1, hard: 1.32, expert: 1.7, adaptive: 1.2,
  };
  const difficultyBonus = Math.round((base + positionBonus) * (difficultyScale[difficulty] - 1));
  const newBestLap = result.bestLap > 0 && (previousBestLap <= 0 || result.bestLap < previousBestLap);
  const bestLapBonus = newBestLap ? 350 : 0;
  const cleanBonus = result.respawns === 0 ? 120 : 0;
  const finishedScale = result.finished ? 1 : 0.45;

  const money = Math.round(
    (base + positionBonus + lapBonus + driftBonus + difficultyBonus + bestLapBonus + cleanBonus) * finishedScale,
  );
  const xp = Math.round(money * 0.6 + result.laps * 12 + (result.finished ? 45 : 0));
  return {
    money,
    xp,
    base,
    positionBonus,
    lapBonus,
    driftBonus,
    difficultyBonus,
    bestLapBonus,
    newBestLap,
  };
}

export { formatTime };
